
import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { DateRange } from "react-day-picker";
import { format } from 'date-fns';
import { ResellerOrder } from '@/types/resellerOrder';
import { SalesData } from '@/utils/licenseService';
import { getRealSalesData } from './sales/SalesDataConnection';
import SalesStats from './sales/SalesStats';
import SalesChart from './sales/SalesChart';
import SalesFilter from './sales/SalesFilter';
import SalesTable from './sales/SalesTable';
import BusinessPlanSalesReport from './BusinessPlanSalesReport';

// Sample data for Reseller Pro sales chart
const chartData = [
  { month: 'Jan', newSales: 5, recurringSales: 12, totalRevenue: 13600 },
  { month: 'Feb', newSales: 8, recurringSales: 17, totalRevenue: 18700 },
  { month: 'Mar', newSales: 10, recurringSales: 25, totalRevenue: 26300 },
  { month: 'Apr', newSales: 15, recurringSales: 35, totalRevenue: 37800 },
  { month: 'May', newSales: 12, recurringSales: 47, totalRevenue: 47200 },
  { month: 'Jun', newSales: 18, recurringSales: 59, totalRevenue: 61500 },
];

interface ResellerSale {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  productName: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  orderDate: Date;
  licenseKey?: string;
  superAdmin?: string;
  businessAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  contactPhone?: string;
  domain?: string;
  plan?: string;
}

const ResellerSalesReport: React.FC = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('reseller-sales');
  
  // Reseller sales state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // State for order details dialog
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ResellerSale | null>(null);
  
  // State for grouping
  const [groupBy, setGroupBy] = useState<'none' | 'superadmin' | 'instance'>('none');
  
  // Load the real sales data
  useEffect(() => {
    const loadSalesData = async () => {
      try {
        setIsLoading(true);
        const data = await getRealSalesData();
        setSalesData(data);
      } catch (error) {
        console.error('Error loading sales data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSalesData();
  }, []);
  
  // Filter sales based on search, status, and date range
  const filteredSales = useMemo(() => {
    return salesData.filter(sale => {
      // Filter by search term
      const searchMatch = 
        sale.invoiceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.superAdminName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.instanceName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by status
      const statusMatch = statusFilter === 'all' || sale.status === statusFilter;
      
      // Filter by date range
      const dateMatch = !dateRange?.from || (
        sale.date >= dateRange.from &&
        (!dateRange.to || sale.date <= dateRange.to)
      );
      
      return searchMatch && statusMatch && dateMatch;
    });
  }, [salesData, searchTerm, statusFilter, dateRange]);
  
  // Calculate stats for the scoreboard
  const stats = useMemo(() => {
    const totalSales = filteredSales.length;
    const paidSales = filteredSales.filter(s => s.status === 'paid').length;
    const pendingSales = filteredSales.filter(s => s.status === 'pending').length;
    const totalRevenue = filteredSales
      .filter(s => s.status === 'paid')
      .reduce((sum, sale) => sum + sale.amount, 0);
    
    return {
      totalSales,
      paidSales,
      pendingSales,
      totalRevenue
    };
  }, [filteredSales]);
  
  // Handle export to CSV
  const handleExportCSV = () => {
    const headers = [
      'Order Number', 'Customer', 'Email', 'Product', 'Amount', 
      'Status', 'Order Date', 'License Key', 'Super Admin'
    ].join(',');
    
    const rows = filteredSales.map(sale => [
      sale.invoiceId,
      sale.superAdminName || 'N/A',
      sale.instanceName || 'N/A',
      sale.planName,
      sale.amount,
      sale.status,
      format(new Date(sale.date), 'yyyy-MM-dd'),
      'N/A', // License key not included in SalesData type
      sale.superAdminName || 'N/A'
    ].join(','));
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'reseller-sales.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Handle order click
  const handleOrderClick = (order: ResellerSale) => {
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  };
  
  return (
    <div className="space-y-6">
      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="reseller-sales">Reseller Pro Sales</TabsTrigger>
          <TabsTrigger value="business-sales">Business Plan Sales</TabsTrigger>
        </TabsList>
        
        <TabsContent value="reseller-sales">
          {/* Reseller Sales Scoreboard */}
          <SalesStats 
            totalSales={stats.totalSales}
            paidSales={stats.paidSales}
            pendingSales={stats.pendingSales}
            totalRevenue={stats.totalRevenue}
            formatCurrency={formatCurrency}
          />
          
          {/* Revenue Chart */}
          <SalesChart data={chartData} formatCurrency={formatCurrency} />
          
          {/* Sales Table */}
          <Card>
            <CardHeader>
              <CardTitle>Reseller Pro Sales</CardTitle>
              <CardDescription>All orders for Reseller Pro Plan licenses</CardDescription>
            </CardHeader>
            <CardContent>
              <SalesFilter
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                dateRange={dateRange}
                setSearchTerm={setSearchTerm}
                setStatusFilter={setStatusFilter}
                setDateRange={setDateRange}
                onExportCSV={handleExportCSV}
                groupBy={groupBy}
                setGroupBy={setGroupBy}
              />
              
              {/* Sales Table */}
              <SalesTable filteredData={filteredSales} groupBy={groupBy} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="business-sales">
          <BusinessPlanSalesReport />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResellerSalesReport;
